import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, LogIn, ArrowLeft, Sparkles, Heart, Star, Phone, Mail } from 'lucide-react'

// Common country codes
const countryCodes = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
]

export default function Login() {
  const navigate = useNavigate()
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email')
  const [formData, setFormData] = useState({
    email: '',
    countryCode: '+91',
    phone: '',
    password: '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({})
  const [isFormValid, setIsFormValid] = useState(false)

  // Real-time validation
  useEffect(() => {
    const errors: { [key: string]: string } = {};

    if (loginMethod === 'email') {
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    } else {
      if (formData.phone && !/^[\d\s\-\(\)]{7,15}$/.test(formData.phone)) {
        errors.phone = 'Please enter a valid phone number (7-15 digits)';
      }
    }

    if (formData.password && formData.password.length > 0 && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    setIsFormValid(
      (loginMethod === 'email' ? formData.email !== '' : formData.phone !== '') &&
      formData.password !== '' &&
      Object.keys(errors).length === 0
    );
  }, [formData, loginMethod]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isFormValid) return

    setLoading(true)
    setError('')

    try {
      const loginData = loginMethod === 'email'
        ? { email: formData.email, password: formData.password }
        : { phone: `${formData.countryCode}${formData.phone}`, password: formData.password }

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      })

      const data = await response.json()

      if (response.ok) {
        localStorage.setItem('token', data.token)
        localStorage.setItem('user', JSON.stringify(data.user))

        // Redirect based on user role
        if (data.user.role === 'ADMIN' || data.user.role === 'admin') {
          navigate('/admin')
        } else {
          navigate('/user/dashboard')
        }
      } else {
        setError(data.error || 'Invalid credentials')
      }
    } catch (error) {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-24 bg-gradient-to-br from-pink-50 via-white to-slate-50 dark:from-slate-900 dark:to-slate-800 relative overflow-hidden flex items-center justify-center">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-24 h-24 bg-gradient-to-br from-purple-200/30 to-pink-300/20 dark:from-purple-500/20 dark:to-pink-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-20 right-20 w-20 h-20 bg-gradient-to-br from-pink-300/20 to-purple-400/10 dark:from-pink-400/10 dark:to-purple-500/0 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/4 w-32 h-32 bg-gradient-to-br from-slate-200/20 to-pink-200/10 dark:from-slate-700/20 dark:to-pink-400/10 rounded-full blur-3xl animate-pulse delay-2000"></div>

        {/* Floating Sparkles */}
        <div className="absolute top-1/4 left-1/3 text-pink-300/40 animate-bounce delay-500">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="absolute top-1/3 right-1/4 text-pink-400/30 animate-bounce delay-1000">
          <Star className="h-3 w-3" />
        </div>
        <div className="absolute bottom-1/3 left-1/5 text-pink-300/40 animate-bounce delay-1500">
          <Heart className="h-4 w-4" />
        </div>
      </div>

      <div className="relative z-10 flex items-center justify-center w-full py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-all duration-300 mb-8 group hover:scale-105"
            >
              <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-2 transition-transform duration-300" />
              <span className="font-medium">Back to Home</span>
            </Link>
            
            <div className="mb-10 animate-fade-in">
              {/* Logo/Icon Area */}
              <div className="mx-auto w-20 h-20 mb-6 shadow-lg rounded-full overflow-hidden">
                <img
                  src="/Logo.png"
                  alt="Sudha's Academy Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              
              <h1 className="text-4xl font-bold font-serif text-slate-900 dark:text-white mb-3 tracking-wide">
                Welcome Back
              </h1>
              <p className="text-lg text-slate-600 dark:text-slate-300 font-light">
                Sign in to your beauty journey
              </p>
              <div className="w-16 h-0.5 bg-gradient-to-r from-purple-400 to-pink-600 mx-auto mt-4"></div>
            </div>
          </div>

          {/* Login Form */}
          <div className="relative">
            {/* Card with enhanced styling */}
            <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-slate-700/50 p-10 relative overflow-hidden">
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-pink-50/50 to-transparent dark:from-pink-900/10 dark:to-transparent rounded-3xl"></div>
              
              <div className="relative z-10">
                <form className="space-y-8" onSubmit={handleSubmit}>
                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-5 py-4 rounded-2xl text-sm font-medium animate-shake">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                        {error}
                      </div>
                    </div>
                  )}

                  {/* Login Method Toggle */}
                  <div className="space-y-4">
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 tracking-wide">
                      Login with
                    </label>
                    <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
                      <button
                        type="button"
                        onClick={() => setLoginMethod('email')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                          loginMethod === 'email'
                            ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-400 shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                      >
                        <Mail className="h-4 w-4" />
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => setLoginMethod('phone')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                          loginMethod === 'phone'
                            ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-400 shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                      >
                        <Phone className="h-4 w-4" />
                        Phone
                      </button>
                    </div>
                  </div>

                  {/* Email Field */}
                  {loginMethod === 'email' && (
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 tracking-wide">
                        Email Address
                      </label>
                      <div className="relative group">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                            focusedField === 'email' || formData.email
                              ? validationErrors.email
                                ? 'border-red-300 ring-2 ring-red-100 dark:border-red-500 dark:ring-red-500/20'
                                : 'border-purple-300 ring-2 ring-purple-100 dark:border-purple-400 dark:ring-purple-500/20'
                              : 'border-slate-300 dark:border-slate-600'
                          } bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none`}
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                        />
                        {validationErrors.email && (
                          <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium animate-slide-in-left">
                            {validationErrors.email}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Phone Field */}
                  {loginMethod === 'phone' && (
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 tracking-wide">
                        Phone Number
                      </label>
                      <div className="flex gap-2">
                        <select
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleChange}
                          className="px-3 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all"
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </select>
                        <div className="relative group flex-1">
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            autoComplete="tel"
                            required
                            className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                              focusedField === 'phone' || formData.phone
                                ? validationErrors.phone
                                  ? 'border-red-300 ring-2 ring-red-100 dark:border-red-500 dark:ring-red-500/20'
                                  : 'border-purple-300 ring-2 ring-purple-100 dark:border-purple-400 dark:ring-purple-500/20'
                                : 'border-slate-300 dark:border-slate-600'
                            } bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none`}
                            placeholder="Enter your phone number"
                            value={formData.phone}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('phone')}
                            onBlur={() => setFocusedField(null)}
                          />
                          {validationErrors.phone && (
                            <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium animate-slide-in-left">
                              {validationErrors.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Password Field */}
                  <div className="space-y-2">
                    <label htmlFor="password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 tracking-wide">
                      Password
                    </label>
                    <div className="relative group">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        className={`w-full px-4 py-3 pr-12 rounded-xl border transition-all duration-200 ${
                          focusedField === 'password' || formData.password
                            ? validationErrors.password
                              ? 'border-red-300 ring-2 ring-red-100 dark:border-red-500 dark:ring-red-500/20'
                              : 'border-purple-300 ring-2 ring-purple-100 dark:border-purple-400 dark:ring-purple-500/20'
                            : 'border-slate-300 dark:border-slate-600'
                        } bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none`}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center group/eye"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-slate-400 dark:text-slate-400 group-hover/eye:text-purple-500 dark:group-hover/eye:text-purple-400 transition-colors duration-200" />
                        ) : (
                          <Eye className="h-5 w-5 text-slate-400 dark:text-slate-400 group-hover/eye:text-purple-500 dark:group-hover/eye:text-purple-400 transition-colors duration-200" />
                        )}
                      </button>
                      {validationErrors.password && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium animate-slide-in-left">
                          {validationErrors.password}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={!isFormValid || loading}
                    className={`group w-full flex justify-center items-center py-4 px-6 border-0 text-base font-semibold rounded-2xl text-white transition-all duration-300 transform ${
                      isFormValid && !loading
                        ? 'bg-gradient-to-r from-purple-500 via-pink-600 to-pink-700 hover:from-purple-600 hover:via-pink-700 hover:to-pink-800 shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]'
                        : 'bg-gradient-to-r from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-700 cursor-not-allowed'
                    } relative overflow-hidden`}
                  >
                    {/* Button shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    
                    {loading ? (
                      <div className="flex items-center relative z-10">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                        <span>Signing you in...</span>
                      </div>
                    ) : (
                      <div className="flex items-center relative z-10">
                        <LogIn className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform duration-200" />
                        <span>Sign In to Your Account</span>
                      </div>
                    )}
                  </button>
                </form>

                {/* Footer Links */}
                <div className="mt-10 text-center space-y-6">
                  <Link
                    to="/forgot-password"
                    className="inline-flex items-center text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-semibold transition-all duration-200 hover:scale-105 group"
                  >
                    <span>Forgot your password?</span>
                    <Sparkles className="h-3 w-3 ml-1 group-hover:animate-spin" />
                  </Link>
                  
                  <div className="flex items-center justify-center space-x-4">
                    <div className="h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-700 to-transparent flex-1"></div>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium tracking-widest uppercase">or</span>
                    <div className="h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-700 to-transparent flex-1"></div>
                  </div>
                  
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    <span>New to Sudha's Academy? </span>
                    <Link
                      to="/user/register"
                      className="font-semibold text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-all duration-200 hover:scale-105 inline-flex items-center group"
                    >
                      <span>Create your account</span>
                      <Heart className="h-3 w-3 ml-1 group-hover:text-red-500 transition-colors duration-200" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="text-center mt-8 animate-fade-in">
            <div className="flex items-center justify-center space-x-6 text-xs text-slate-500 dark:text-slate-400">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Secure Login</span>
              </div>
              <div className="flex items-center space-x-1">
                <Star className="h-3 w-3 text-pink-500 dark:text-pink-400" />
                <span>Trusted by 1000+ Students</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}